/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import React, { useEffect, useState } from 'react'
import * as HighCharts from 'highcharts'
import { Card } from '@scuf/common'
import { Box, CircularProgress, styled } from '@mui/material'
import HighchartsReact from 'highcharts-react-official'
import DarkUnica from 'highcharts/themes/dark-unica'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { type Dayjs } from 'dayjs'
import LanguageStrings from '../../../i18n/locales'
import AuthError from '../../../components/ErrorComponents/AuthError'
import { DesktopDateTimePicker } from '@mui/x-date-pickers'
import { type AssetType } from '../../../utils/NetworkGraphUtils'
import { type signalState } from '../redux/slices/cellularSignalSlice'
import { getDatePickerInfo } from '../../../utils/datePickerInfo'

interface ParentProps {
  fetchSignalData: (tenant: string, assetId: string, from: string, to: string, assetType: AssetType) => void
  tenant: string
  signalData: signalState
  assetId: string
  assetType: AssetType
}

const CellularSignalStrength = (props: ParentProps): JSX.Element => {
  const CellularStrings = LanguageStrings().DeviceDetailsStrings.Cellular
  let paramAssetId = sessionStorage.getItem('paramAssetId')
  // istanbul ignore next
  if (paramAssetId === null) {
    paramAssetId = ''
  }
  const defaultDate = {
    end: dayjs(new Date()),
    start: dayjs(new Date()).subtract(7, 'day').clone().hour(0).minute(0).second(0)
  }
  const [start, setStart] = useState<Dayjs | null | undefined>(defaultDate.start)
  const [end, setEnd] = useState<Dayjs | null | undefined>(defaultDate.end)
  const [isStartModified, setIsStartModified] = useState(false)
  const [isEndModified, setIsEndModified] = useState(false)
  DarkUnica(HighCharts)

  // istanbul ignore next
  const handleDateChange = (type: string, date: dayjs.Dayjs): void => {
    if (type === 'start') {
      setIsStartModified(true)
      setStart(date)
      if (!isEndModified) {
        setEnd(date.clone().add(7, 'day'))
      }
    } else if (type === 'end') {
      setIsEndModified(true)
      setEnd(date)
      if (!isStartModified) {
        setStart(date.clone().subtract(7, 'day'))
      }
    }
  }

  const fetchSignaldata = (): any => {
    // istanbul ignore next
    if (paramAssetId !== undefined && start !== (undefined) && start !== null && end !== undefined && end !== null && start <= end) {
      const startMoment = start
        .clone()
        .set('second', 0)
      const endMoment = end
        .clone()
        .set('second', 59)
      props.fetchSignalData(props.tenant, props.assetId, startMoment.format('YYYY-MM-DDTHH:mm:ssZ'), endMoment.format('YYYY-MM-DDTHH:mm:ssZ'), props.assetType)
    }
  }
  useEffect(() => {
    fetchSignaldata()
  }, [start, end])

  const WhiteDatePicker = styled(DesktopDateTimePicker)({
    '& input': {
      color: '#D0D0D0',
      fontSize: '14px',
      paddingRight: 0
    }
  })

  const CustomLocalizationProviderWrapper = styled('div')({
    backgroundColor: '#505050',
    border: 'none',
    borderRadius: '4px'
  })

  function getGraph (): JSX.Element {
    if (props.signalData.isLoading) {
      return <CircularProgress />
    } else if (props.signalData.httpStatus === 401) {
      return (
      <div className='authError'><AuthError errorMessage="Unauthorized"/></div>
      )
    } else if (props.signalData.httpStatus === 403) {
      return (
            <div className='authError'><AuthError errorMessage="accessForbidden"/></div>
      )
    } else if ((props.signalData.channelDataOptions === null && props.signalData.loadingError !== null && props.signalData.httpStatus === 200)) {
      return (
        <div className='authError'><AuthError errorMessage="NoDataPresent"/></div>
      )
    } else if (props.signalData.rawData) {
      return (
        <div className='authError'><AuthError errorMessage="NoDataPresent"/></div>
      )
    } else if (
      props.signalData.channelDataOptions !== undefined &&
      props.signalData.channelDataOptions !== null
    ) {
      return (
        <HighchartsReact
          highcharts={HighCharts}
          options={props.signalData.channelDataOptions?.consumptionChartOptions}
        />
      )
    } else {
      return (
        <div className='authError'><AuthError errorMessage="cannotFetch" retry={fetchSignaldata}/></div>
      )
    }
  }

  return (
    <div className='devicedetails'>
      <Box sx={{ width: '100%', padding: '0 1em' }}>
        <Card>
        <Card.Header title={CellularStrings.title} style={{ fontSize: '14px' }}>
          <span style={{ paddingRight: '1em', display: 'flex', alignItems: 'center' }}>
            <span style={{ paddingRight: '6px', fontSize: '14px' }}>
            {CellularStrings.StartDate}
            </span>
            <span className='datePicker-deviceDetails'>
              <CustomLocalizationProviderWrapper>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                <WhiteDatePicker minDate={(end ?? dayjs()).clone().subtract(3, 'month')} disableFuture closeOnSelect={false} ampm={false} timeSteps={{ minutes: 1 }} slotProps={{ textField: { size: 'small' }, actionBar: { actions: ['cancel', 'accept'] }, field: { readOnly: true } }} value={start} onAccept={(val: any) => { handleDateChange('start', val) }} />
                </LocalizationProvider>
              </CustomLocalizationProviderWrapper>
            </span>
          </span>
          <span style={{ paddingRight: '1em', display: 'flex', alignItems: 'center' }}>
            <span style={{ paddingRight: '6px', fontSize: '14px' }}>
            {CellularStrings.EndDate}
            </span>
            <span className='datePicker-deviceDetails'>
              <CustomLocalizationProviderWrapper>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <WhiteDatePicker disableFuture closeOnSelect={false} ampm={false} timeSteps={{ minutes: 1 }} slotProps={{ textField: { size: 'small' }, actionBar: { actions: ['cancel', 'accept'] }, field: { readOnly: true } }} minDate={start} value={end} onAccept={(val: any) => { handleDateChange('end', val) }} />
                </LocalizationProvider>
              </CustomLocalizationProviderWrapper>
            </span>
            <span style={{ marginRight: '2.5rem', marginBottom: '1.5rem' }}>
                  {getDatePickerInfo()}
            </span>
          </span>
        </Card.Header>
        <Card.Content>
          {getGraph()}
          </Card.Content>
        </Card>
      </Box>
    </div>
  )
}
export default CellularSignalStrength
